/**
 * Which global-modal id and URL params carry an entity's "History & Audit" modal.
 *
 * Shared by the action handler that opens the modal
 * (`components/shared/UniversalDetailPage/actions/audit-actions.ts`) and by the
 * modal itself (`EntityAuditModal.tsx`), so the id and the param spelling
 * cannot drift between the two. Pure data — no React, no server imports.
 *
 * Registration lives in three places and all three are load-bearing:
 *  - `MODAL_IDS.audit` + `GLOBAL_MODAL_IDS` (packages/ui) — the stack id
 *  - `MODAL_URL_CONFIG` (lib/modals/modal-url-config/{contact,company}-modals)
 *    — makes it a history-backed PLACE
 *  - `DetailPageModals.tsx` — the JSX mount (ACTION_MODAL_MOUNTS)
 */

import { MODAL_IDS } from "@west-tennessee-consulting/ui";

/** Entities whose history modal exists today. Subset of `AuditableEntityType`. */
export const AUDIT_HISTORY_ENTITY_TYPES = ["contacts", "companies"] as const;

export type AuditHistoryEntityType = (typeof AUDIT_HISTORY_ENTITY_TYPES)[number];

export interface AuditModalDescriptor {
  /** Global-modal-stack id, also the `?modal=` value. */
  modalId: string;
  /**
   * The entity-id param spellings this modal accepts, plural first. Must match
   * the `pluralParam` / `singularParam` of its `MODAL_URL_CONFIG` entry — the
   * history sync opens the modal with `{ [pluralParam]: id }` as its payload.
   */
  idParams: readonly [plural: string, singular: string];
  /** Singular human label, used when the record's own name is not known. */
  entityLabel: string;
}

export const AUDIT_HISTORY_MODALS = {
  contacts: {
    modalId: MODAL_IDS.audit.contactsHistory,
    idParams: ["contactsId", "contactId"],
    entityLabel: "Contact",
  },
  companies: {
    modalId: MODAL_IDS.audit.companiesHistory,
    idParams: ["companiesId", "companyId"],
    entityLabel: "Company",
  },
} as const satisfies Record<AuditHistoryEntityType, AuditModalDescriptor>;

export function isAuditHistoryEntityType(
  value: string
): value is AuditHistoryEntityType {
  return (AUDIT_HISTORY_ENTITY_TYPES as readonly string[]).includes(value);
}

/**
 * Pull the entity id out of a modal-stack data payload. The payload arrives
 * either from the action handler (which writes the plural spelling) or from
 * `useModalHistorySync` on a deep link / Forward (which writes whichever
 * spelling `extractModalParams` produced — also the plural one). The singular
 * spelling is accepted so a hand-typed URL still works.
 */
export function readAuditEntityId(
  entityType: AuditHistoryEntityType,
  data: Record<string, unknown> | undefined
): string | null {
  if (!data) return null;
  for (const key of AUDIT_HISTORY_MODALS[entityType].idParams) {
    const value = data[key];
    if (typeof value === "string" && value.length > 0) return value;
  }
  return null;
}
